/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 20/04/22
 */

import {
    withCurrentAssignment,
    WithCurrentAssignmentProps,
} from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import LocalAdminListTable from '@/components/pages/Admin/LocalAdmin/LocalAdminListTable';
import UserAssignmentPicker from '@/components/pages/Admin/Pickers/UserAssignmentPicker';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { FacultyAdmin } from '@/wpt-lib/interfaces/FacultyAdmin';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { User } from '@/wpt-lib/interfaces/User';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { Text } from '@chakra-ui/react';
import React, { useLayoutEffect, useState } from 'react';
import { Tile } from '@/components/Tile';
import { isNullish } from '@/wpt-lib/utils/isNullish';

export interface FacultyAdminEditorProps extends WithCurrentAssignmentProps {
    facultyId: number;
    organisationId?: number;
}

interface UpdateFacultyAdminsDTO {
    adminAssignmentIds: number[];
}

const LocalAdminEditor = ({ facultyId, organisationId, currentAssignment }: FacultyAdminEditorProps) => {
    const { error, loading, responseData: facultyAdmins, makeRequest } = useGetRequest<ListResponse<FacultyAdmin>>();
    const {
        error: submitAdminsError,
        loading: submittingAdmins,
        makeRequest: updateAdmins,
        responseData: updatedAdmins,
        clear,
    } = usePutRequest<FacultyAdmin[], UpdateFacultyAdminsDTO>();

    const [tempAdminAssignmentsList, setTempAdminAssignmentsList] = useState<Assignment[]>();

    useAsyncEffect(async () => {
        try {
            clear();
            await makeRequest(
                `${!isNullish(organisationId) ? 'organisation' : 'faculty'}/${organisationId ?? facultyId}/admins`,
                {
                    params: {
                        assignment: currentAssignment?.id,
                    },
                },
            );
        } catch (e) {
            // handled by hook
        }
    }, [facultyId, organisationId, currentAssignment]);

    useLayoutEffect(() => {
        if (updatedAdmins) {
            setTempAdminAssignmentsList(updatedAdmins.map((admin) => admin.assignment));
        } else if (facultyAdmins) {
            setTempAdminAssignmentsList(facultyAdmins.items.map((admin) => admin.assignment));
        }
    }, [facultyAdmins, updatedAdmins]);

    const handleSubmit = async (assignments: Assignment[]) => {
        await updateAdmins(
            `${!isNullish(organisationId) ? 'organisation' : 'faculty'}/${organisationId ?? facultyId}/admins`,
            {
                adminAssignmentIds: assignments.map((assignment) => assignment.id),
            },
            {
                params: {
                    assignment: currentAssignment?.id,
                },
            },
        );
    };

    const onAddAssignment = async (selectedAssignment: Assignment, selectedUser: User): Promise<boolean> => {
        if (!tempAdminAssignmentsList || !tempAdminAssignmentsList.find((a) => a.id === selectedAssignment.id)) {
            try {
                await handleSubmit([
                    ...(tempAdminAssignmentsList || []),
                    { ...selectedAssignment, user: selectedUser },
                ]);
                return true;
            } catch (e) {
                // handled by hook
            }
        }
        return false;
    };

    const onRemoveAssignment = async (assignment: Assignment) => {
        try {
            await handleSubmit(
                (tempAdminAssignmentsList || []).filter((adminAssignment) => adminAssignment.id !== assignment.id),
            );
        } catch (e) {
            // handled by hook
        }
    };

    return (
        <Tile colspan={3} heading={'Local Administrators'} borderWidth={1} borderColor={'gray.100'} rounded={'lg'}>
            <Text>
                This section allows you to add local administrators. These administrators will be able to assign new
                supervisors to individuals, access and review workplans.
            </Text>
            <StateDisplay
                data={tempAdminAssignmentsList}
                loading={loading}
                error={!!error}
                errorRender={() => <SimpleAlert variant="subtle">Could not load Faculty Admins</SimpleAlert>}
                render={(data) => {
                    return (
                        <>
                            <UserAssignmentPicker
                                currentAssignment={currentAssignment}
                                onSelectAssignment={onAddAssignment}
                                loading={submittingAdmins}
                            />
                            <LocalAdminListTable
                                admins={data}
                                onRemove={onRemoveAssignment}
                                actionsDisabled={submittingAdmins}
                            />
                            {submitAdminsError && (
                                <SimpleAlert variant="subtle">Could not update faculty admins.</SimpleAlert>
                            )}
                        </>
                    );
                }}
            />
        </Tile>
    );
};

export default withCurrentAssignment(LocalAdminEditor);
